// /home/labdoo/zitech-nexus/frontend/src/pages/ServiceRequest.tsx

import React, { useState, useEffect, useRef, JSX } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Website/Navbar';
import Footer from '../components/Website/Footer';
import { serviceAPI } from '../services/api';
import './ServiceRequest.css';

interface ServiceRequestForm {
  serviceName: string;
  customerName: string;
  email: string;
  phone: string;
  companyName: string;
  message: string;
  selectedTechnologies: string[];
}

interface FileAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface FormErrors {
  message?: string;
  technologies?: string;
  companyName?: string;
  submit?: string;
}

interface SubmitSuccess {
  user_exists: boolean;
  temporary_password?: string;
  user?: any;
  access_token?: string;
  refresh_token?: string;
  message?: string;
}

const ServiceRequest: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [submitted, setSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState<SubmitSuccess | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<ServiceRequestForm>({
    serviceName: '',
    customerName: '',
    email: '',
    phone: '',
    companyName: '',
    message: '',
    selectedTechnologies: [],
  });

  const availableServices = [
    'Web Development',
    'Mobile App Development',
    'Software Integration',
    'IT Consultancy',
    'Training Programs',
    'Cloud Services',
    'Class Marks Record'
  ];

  const technologiesList = [
    'React', 'Angular', 'Vue.js', 'Django', 'Node.js', 'Python', 
    'Java', 'Spring Boot', 'Laravel', 'PostgreSQL', 'MongoDB', 'MySQL',
    'AWS', 'Docker', 'Kubernetes', 'React Native', 'Flutter', 'Swift',
    'Kotlin', 'Git', 'TypeScript', 'JavaScript', 'HTML/CSS', 'PHP'
  ];

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const service = params.get('service');
    if (service) {
      setFormData(prev => ({ ...prev, serviceName: service }));
    }
  }, [location]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    if (name === 'message' && errors.message) {
      setErrors({ ...errors, message: undefined });
    }
    if (name === 'companyName' && errors.companyName) {
      setErrors({ ...errors, companyName: undefined });
    }
    if (errors.submit) {
      setErrors({ ...errors, submit: undefined });
    }
  };

  const handleTechnologyToggle = (tech: string) => {
    setFormData(prev => {
      const isSelected = prev.selectedTechnologies.includes(tech);
      const updatedTechnologies = isSelected
        ? prev.selectedTechnologies.filter(t => t !== tech)
        : [...prev.selectedTechnologies, tech];
      
      if (updatedTechnologies.length > 0 && errors.technologies) {
        setErrors({ ...errors, technologies: undefined });
      }
      
      return { ...prev, selectedTechnologies: updatedTechnologies };
    });
  };

  const countCharacters = (text: string): number => {
    return text.trim().length;
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const maxSize = 10 * 1024 * 1024;
      const validFiles: FileAttachment[] = [];
      
      Array.from(files).forEach(file => {
        if (file.size > maxSize) {
          setErrors(prev => ({ ...prev, submit: `File ${file.name} exceeds 10MB limit` }));
        } else {
          validFiles.push({
            id: `${Date.now()}-${Math.random()}`,
            name: file.name,
            size: file.size,
            type: file.type,
            file: file,
          });
        }
      });
      
      setAttachments(prev => [...prev, ...validFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(att => att.id !== id));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileName: string): JSX.Element => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension || '')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 8H16M8 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    if (['pdf'].includes(extension || '')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4H10L14 8V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 12H12M8 16H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    if (['doc', 'docx'].includes(extension || '')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 4H10L14 8V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 12H12M8 16H10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    if (['xls', 'xlsx'].includes(extension || '')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none"/>
          <path d="M8 8H16M8 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    }
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4H10L14 8V20H4V4Z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
      </svg>
    );
  };

  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    const characterCount = countCharacters(formData.message);
    
    if (!formData.serviceName) {
      newErrors.submit = 'Please select a service';
      setErrors(newErrors);
      return false;
    }
    
    if (!formData.customerName.trim()) {
      newErrors.submit = 'Please enter your full name';
      setErrors(newErrors);
      return false;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      newErrors.submit = 'Please enter a valid email address';
      setErrors(newErrors);
      return false;
    }
    
    const phoneRegex = /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,5}[-\s.]?[0-9]{1,5}$/;
    if (!phoneRegex.test(formData.phone)) {
      newErrors.submit = 'Please enter a valid phone number';
      setErrors(newErrors);
      return false;
    }
    
    if (!formData.companyName.trim()) {
      const label = formData.serviceName === 'Class Marks Record' ? 'School Name' : 'Company Name';
      newErrors.companyName = `Please enter the ${label}`;
    }
    
    if (characterCount < 100) {
      newErrors.message = `Project details must be at least 100 characters. Current: ${characterCount} characters`;
    }
    
    if (formData.serviceName === 'Training Programs' && formData.selectedTechnologies.length === 0) {
      newErrors.technologies = 'Please select at least one technology for training';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    const formDataToSend = new FormData();
    formDataToSend.append('service_name', formData.serviceName);
    formDataToSend.append('customer_name', formData.customerName.trim());
    formDataToSend.append('email', formData.email.trim());
    formDataToSend.append('phone', formData.phone.trim());
    formDataToSend.append('company_name', formData.companyName.trim());
    formDataToSend.append('message', formData.message.trim());
    formDataToSend.append('selected_technologies', JSON.stringify(formData.selectedTechnologies));
    
    attachments.forEach(attachment => {
      formDataToSend.append('attachments', attachment.file);
    });
    
    try {
      const response = await serviceAPI.createServiceRequest(formDataToSend);
      const data = response.data;
      
      setSubmitSuccess({
        user_exists: data.user_exists,
        temporary_password: data.temporary_password,
        user: data.user,
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        message: data.message
      });
      
      if (data.access_token && data.refresh_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        
        if (data.user) {
          localStorage.setItem('user', JSON.stringify(data.user));
        }
      }
      
      setSubmitted(true);
      
      setTimeout(() => {
        if (data.user_exists) {
          window.location.href = '/login';
        } else {
          window.location.href = '/dashboard';
        }
      }, 5000);
    } catch (error: any) {
      console.error('Error submitting service request:', error);
      
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.response?.data?.detail ||
                          'Failed to submit service request. Please try again.';
      
      setErrors({ submit: errorMessage });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const characterCount = countCharacters(formData.message);
  const isTrainingService = formData.serviceName === 'Training Programs';
  const isClassMarksService = formData.serviceName === 'Class Marks Record';
  const isCharacterCountValid = characterCount >= 100;

  const companyNameLabel = isClassMarksService ? 'School Name' : 'Company Name';
  const companyNamePlaceholder = isClassMarksService 
    ? 'Enter your school name' 
    : 'Enter your company name';

  // SVG Icons
  const Icons = {
    Upload: () => (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
        <polyline points="17 8 12 3 7 8"/>
        <line x1="12" y1="3" x2="12" y2="15"/>
      </svg>
    ),
    Check: () => (
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
        <polyline points="22 4 12 14.01 9 11.01"/>
      </svg>
    ),
  };

  return (
    <>
      <Navbar />
      <div className="sr-page">
        <div className="sr-container">
          <div className="sr-header">
            <div className="sr-header-badge">Get Started</div>
            <h1 className="sr-title">Request a <span>Service</span></h1>
            <p className="sr-subtitle">
              Fill out the form below and we'll get back to you within 24 hours
            </p>
          </div>

          {submitted ? (
            <div className="sr-success-wrapper">
              <div className="sr-success-card">
                <div className="sr-success-icon">
                  <Icons.Check />
                </div>
                <h2 className="sr-success-title">
                  {submitSuccess?.user_exists 
                    ? 'Service Request Submitted!' 
                    : 'Account Created & Request Submitted!'}
                </h2>
                <p className="sr-success-text">
                  {submitSuccess?.user_exists 
                    ? 'Thank you for your interest. Our team will contact you shortly.'
                    : `Thank you ${formData.customerName}! An account has been created for you.`}
                </p>
                {!submitSuccess?.user_exists && submitSuccess?.temporary_password && (
                  <div className="sr-temp-password">
                    <strong>Temporary Password:</strong> {submitSuccess.temporary_password}
                    <br />
                    <small>Please save this password. You can change it after logging in.</small>
                  </div>
                )}
                <p className="sr-redirect">
                  Redirecting to {submitSuccess?.user_exists ? 'login' : 'dashboard'} in 5 seconds...
                </p>
                <button 
                  className="sr-home-btn" 
                  onClick={() => window.location.href = submitSuccess?.user_exists ? '/login' : '/dashboard'}
                >
                  {submitSuccess?.user_exists ? 'Go to Login' : 'Go to Dashboard'}
                </button>
              </div>
            </div>
          ) : (
            <form className="sr-form" onSubmit={handleSubmit}>
              {errors.submit && (
                <div className="sr-error-alert">
                  <span className="sr-error-icon">⚠️</span>
                  {errors.submit}
                </div>
              )}

              <div className="sr-form-section">
                <h3 className="sr-section-title">Service Information</h3>
                <div className="sr-row">
                  <div className="sr-group">
                    <label className="sr-label">Service Required *</label>
                    <select
                      name="serviceName"
                      className="sr-input"
                      value={formData.serviceName}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">-- Select a Service --</option>
                      {availableServices.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div className="sr-group">
                    <label className="sr-label">Full Name *</label>
                    <input
                      type="text"
                      name="customerName"
                      className="sr-input"
                      placeholder="Enter your full name"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="sr-row">
                  <div className="sr-group">
                    <label className="sr-label">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      className="sr-input"
                      placeholder="Enter your email address"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="sr-group">
                    <label className="sr-label">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      className="sr-input"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="sr-form-section">
                <h3 className="sr-section-title">Company Details</h3>
                <div className="sr-group">
                  <label className="sr-label">
                    {companyNameLabel} *
                  </label>
                  <input
                    type="text"
                    name="companyName"
                    className={`sr-input ${errors.companyName ? 'sr-error' : ''}`}
                    placeholder={companyNamePlaceholder}
                    value={formData.companyName}
                    onChange={handleInputChange}
                    required
                  />
                  {isClassMarksService && (
                    <p className="sr-hint">Please provide the school name for the marks record</p>
                  )}
                  {errors.companyName && (
                    <div className="sr-error-text">{errors.companyName}</div>
                  )}
                </div>
              </div>

              <div className="sr-form-section">
                <h3 className="sr-section-title">Project Details</h3>
                
                {isTrainingService && (
                  <div className="sr-group">
                    <label className="sr-label">Technologies for Training *</label>
                    <p className="sr-tech-hint">Select the technologies you want to be trained on</p>
                    <div className="sr-tech-grid">
                      {technologiesList.map(tech => (
                        <label key={tech} className="sr-tech-item">
                          <input
                            type="checkbox"
                            checked={formData.selectedTechnologies.includes(tech)}
                            onChange={() => handleTechnologyToggle(tech)}
                          />
                          <span className="sr-tech-name">{tech}</span>
                        </label>
                      ))}
                    </div>
                    {errors.technologies && (
                      <div className="sr-error-text">{errors.technologies}</div>
                    )}
                  </div>
                )}

                <div className="sr-group">
                  <label className="sr-label">
                    {isClassMarksService ? 'Examination Details *' : 'Project Details *'}
                    <span className="sr-label-hint"> (Minimum 100 characters)</span>
                  </label>
                  <textarea
                    name="message"
                    className={`sr-textarea ${errors.message ? 'sr-error' : ''}`}
                    rows={8}
                    placeholder={
                      isClassMarksService 
                        ? 'Please describe the examination details including subjects, number of students, and any specific requirements...'
                        : 'Please describe your project requirements, goals, and any specific features you need in detail...'
                    }
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                  <div className="sr-char-count">
                    <span className={isCharacterCountValid ? 'sr-char-valid' : 'sr-char-invalid'}>
                      Characters: {characterCount} / 100
                    </span>
                    {isCharacterCountValid && <span className="sr-char-check">✓ Minimum reached</span>}
                  </div>
                  {errors.message && (
                    <div className="sr-error-text">{errors.message}</div>
                  )}
                </div>

                <div className="sr-group">
                  <label className="sr-label">
                    {isClassMarksService ? 'Upload Marks Excel File' : 'Attachments'}
                    <span className="sr-label-hint"> (Optional)</span>
                  </label>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    multiple
                    id="sr-file-input"
                    className="sr-file-hidden"
                    accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.zip"
                  />
                  
                  <button
                    type="button"
                    className="sr-upload-btn"
                    onClick={triggerFileUpload}
                  >
                    <Icons.Upload />
                    <span>
                      {isClassMarksService ? 'Upload Excel File or Documents' : 'Click to upload documents or images'}
                    </span>
                  </button>
                  
                  <p className="sr-file-hint">
                    {isClassMarksService 
                      ? 'Upload Excel files (.xlsx, .xls) for marks data, or other supporting documents. Max 10MB each.'
                      : 'Supported formats: JPG, PNG, GIF, PDF, DOC, DOCX, XLS, XLSX, ZIP (Max 10MB each)'
                    }
                  </p>
                  
                  {attachments.length > 0 && (
                    <div className="sr-attachments">
                      {attachments.map((file) => (
                        <div key={file.id} className="sr-attachment">
                          <span className="sr-attachment-icon">{getFileIcon(file.name)}</span>
                          <div className="sr-attachment-info">
                            <span className="sr-attachment-name">{file.name}</span>
                            <span className="sr-attachment-size">{formatFileSize(file.size)}</span>
                          </div>
                          <button
                            type="button"
                            className="sr-remove-attachment"
                            onClick={() => removeAttachment(file.id)}
                            aria-label="Remove attachment"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="sr-actions">
                <button 
                  type="button" 
                  className="sr-cancel-btn" 
                  onClick={() => navigate('/')} 
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="sr-submit-btn" 
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServiceRequest;